import type { UseFormReturn } from 'react-hook-form';

import { EMPTY_POST_FORM } from '@/features/post-editor/constants/postForm';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { PostFeedType } from '@/features/posts/types/postFeed';

/**
 * The post form splits its fields into two classes:
 *
 * - **Content** the user authored (`title`, `text`, `poll`, and the media
 *   handled separately by `useMediaPlaceholders`). This is never reset
 *   automatically — switching the channel keeps what the user has written.
 * - **Context** that only makes sense for a specific channel. When the channel
 *   changes, the dependent fields are reset to their defaults so no stale
 *   value is submitted (e.g. an "official" category on a Gossip).
 *
 * Keeping the reset rules here — one place per field class — means the same
 * kind of field is always reset by the same trigger, instead of scattering
 * ad-hoc `setValue(..., undefined)` calls across handlers. Fields are restored
 * to their {@link EMPTY_POST_FORM} defaults (a single source of truth), so e.g.
 * a happening keeps its pre-filled start date.
 */

const CHANNEL_FIELD_DEFAULTS = {
  categories: EMPTY_POST_FORM.categories,
  startDate: EMPTY_POST_FORM.startDate,
  endDate: EMPTY_POST_FORM.endDate,
  allDay: EMPTY_POST_FORM.allDay,
  location: EMPTY_POST_FORM.location,
  suggestionCategory: EMPTY_POST_FORM.suggestionCategory,
  marketType: EMPTY_POST_FORM.marketType,
  dorfHelferType: EMPTY_POST_FORM.dorfHelferType
} as const satisfies Partial<PostFormValues>;

const resetFields = (
  form: UseFormReturn<PostFormValues>,
  defaults: Partial<PostFormValues>
): void => {
  const fields = Object.keys(defaults) as (keyof PostFormValues)[];
  for (const field of fields) {
    form.setValue(field, defaults[field]);
  }
  // Only re-validate the fields that were actually reset. Validating the whole
  // form here would surface unrelated, untouched-field errors (e.g. the
  // organization `geoAreaIds` "required" message) the moment the user switches
  // channels, before they ever interacted with those fields.
  void form.trigger(fields);
};

/**
 * Resets the fields that only apply to a specific channel (happening date,
 * location, suggestion category, market/special sub-type, official category)
 * to their defaults. Called on every channel switch. Content fields (`text`,
 * `poll`) are kept.
 */
export const resetChannelDependentFields = (
  form: UseFormReturn<PostFormValues>
): void => {
  resetFields(form, CHANNEL_FIELD_DEFAULTS);
};

/**
 * Reconciles a requested channel against the channels the current sender may
 * actually use: if the requested channel is not available (e.g. the org-only
 * NEWS_ITEM while sending as a person), fall back to the first available
 * channel, or GOSSIP as a last resort. Data-driven, so it needs no per-channel
 * special casing.
 */
export const resolveAllowedChannel = (
  requested: PostFeedType,
  availableChannels: PostFeedType[]
): PostFeedType => {
  if (availableChannels.includes(requested)) {
    return requested;
  }
  return availableChannels[0] ?? PostFeedType.GOSSIP;
};
